import { action, computed, observable } from 'mobx'
import { makePersistable } from 'mobx-persist-store'

export default class ThemeStore {
  rootStore = null

  @observable accessor theme = 'dark'
  @observable accessor isDarkTheme = true

  constructor(rootStore) {
    this.rootStore = rootStore
    this.rootStore.registerAfterConstructor(this.#_afterConstructor)
    this.rootStore.registerDeconstructor(this.#_deconstructor)
  }

  #_afterConstructor = async () => {
    await makePersistable(this, {
      name: 'theme',
      properties: ['theme'],
    })

    this.#migrate()
    window
      .matchMedia('(prefers-color-scheme: dark)')
      .addEventListener('change', this.#update, { passive: true })
    this.#update()
  }

  #_deconstructor = () => {
    window.matchMedia('(prefers-color-scheme: dark)').removeEventListener('change', this.#update)
  }

  @computed
  get isLightTheme() {
    return !this.isDarkTheme
  }

  @computed
  get prefersDarkTheme() {
    return this.theme === 'dark'
  }

  @computed
  get prefersLightTheme() {
    return this.theme === 'light'
  }

  @computed
  get prefersSystemTheme() {
    return this.theme === 'system'
  }

  @action
  setTheme = x => {
    switch (x.target.dataset.value) {
      case 'system':
        this.theme = 'system'
        break

      case 'light':
        this.theme = 'light'
        break

      case 'dark':
        this.theme = 'dark'
        break

      default:
        console.error(`[Theme] Invalid theme ${x}`)

        return
    }

    this.#update()
  }

  @action
  #migrate = () => {
    const theme = localStorage.getItem('theme')
    if (!theme || theme.startsWith('{')) return

    this.theme = theme
    localStorage.removeItem('theme')
  }

  @action
  #update = () => {
    this.isDarkTheme =
      this.theme === 'dark' ||
      (this.theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)

    document.documentElement.classList.toggle('dark', this.isDarkTheme)
  }
}
